import React, { useState } from 'react';
import { X, IndianRupee, Gift, Zap, Calendar } from 'lucide-react';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Pricing & Plans</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close pricing modal"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Free Trial */}
          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Zap className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-green-900">Free Trial</h3>
              <p className="text-sm text-green-700 mt-1">
                Start your coaching journey with 1 month OR 7 sessions (whichever finishes first)
              </p>
            </div>
          </div>

          {/* Monthly Plan */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <IndianRupee className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">Monthly Subscription</h3>
              <p className="text-lg font-bold text-blue-900 mt-1">₹99/month <span className="text-sm font-normal text-blue-700">+ 18% GST</span></p>
              <div className="text-sm text-blue-700 space-y-1 mt-2">
                <p>• 7 coaching sessions per month</p>
                <p>• Unused sessions carry forward (up to 3 months)</p>
                <p>• Maximum 21 carry-forward sessions</p>
              </div>
            </div>
          </div>

          {/* Extra Sessions */}
          <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <Calendar className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-900">Extra Sessions</h3>
              <p className="text-lg font-bold text-orange-900 mt-1">₹40 for 3 sessions <span className="text-sm font-normal text-orange-700">+ 18% GST</span></p>
              <p className="text-sm text-orange-700">
                Need more coaching? Purchase additional sessions anytime
              </p>
            </div>
          </div>

          {/* Gift Subscriptions */}
          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Gift className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900">Gift Subscriptions</h3>
              <p className="text-sm text-purple-700 mt-1">
                Gift annual subscriptions to colleagues, friends, or family members
              </p>
            </div>
          </div>

          {/* Footer Note */}
          <div className="text-xs text-slate-500 text-center pt-4 border-t border-slate-200">
            Cancel anytime. If membership is discontinued, unused sessions expire automatically.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
